import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SurveyService } from '../../services/surveys.service';
import { SurveyDetailsDialogComponent } from './survey-details-dialog.component';

export interface StatementFormData {
  // A. IDENTITÀ
  projectTitle: string;
  therapeuticArea: string;
  consultationType: {
    asynchronousAdvisoryBoard: boolean;
    delphi: boolean;
    modifiedDelphi: boolean;
    miniDelphi: boolean;
    structuredExpertSurvey: boolean;
    other: string;
  };
  surveyObjective: string;
  sessionFocus: string;
  targetAudience: {
    specialties: string[];
    otherSpecialty: string;
    kol: boolean;
    expertClinicians: boolean;
    mix: boolean;
    hub: boolean;
    spoke: boolean;
    mixedSetting: boolean;
    geography: string;
  };

  // B. PICO / PICOST
  pico: {
    population: string;
    intervention: string;
    comparator: string;
    outcomes: string;
    studies: string;
    timeframe: string;
  };

  // C. CONTESTO CLINICO & UNMET NEEDS
  clinicalContext: {
    patientJourney: string;
    unmetNeeds: string;
  };

  // D. EVIDENZE DA CONSIDERARE
  evidence: {
    anchorStudies: Array<{
      reference: string;
      relevance: string;
    }>;
    evidenceTypes: {
      guidelines: boolean;
      rct: boolean;
      phase2: boolean;
      phase1: boolean;
      realWorld: boolean;
      comparative: boolean;
      biological: boolean;
    };
    exclusions: string;
    previousSurveys: boolean;
    selectedPreviousSurveys: number[];
  };

  // E. OUTPUT CONSTRAINTS
  targetStatements: number;

  // F. COMPLIANCE & STILE
  compliance: {
    strictEvidenceMode: boolean;
    minimizeOffLabelTone: boolean;
    epistemicTransparency: 'base' | 'standard' | 'advanced';
  };

  // G. REGOLE EDITORIALI AVANZATE
  editorialRules: {
    toneNeutral: boolean;
    toneDiscussion: boolean;
    toneNonPromotional: boolean;
    toneNoSuperlatives: boolean;
    evidenceGrading: string;
    granularitySingleConcept: boolean;
    granularityNoRedundancy: boolean;
    glossary: string;
  };
}

@Component({
  selector: 'app-statement-generator-form',
  templateUrl: './statement-generator-form.component.html',
  styleUrls: ['./statement-generator.component.scss'],
  standalone: false
})
export class StatementGeneratorFormComponent implements OnInit {

  @Input() projectId: number;
  @Input() projectData: any;
  @Input() picoData: any;
  @Input() currentPicoTabData: any; // live PICO tab values (patients/intervention/comparison/outcomes)
  @Input() previousSurveysAvailable: any[] = [];
  @Input() existingPicos: any[] = [];
  @Input() existingLiterature: any[] = [];

  @Output() formSubmitted = new EventEmitter<StatementFormData>();
  @Output() formCanceled = new EventEmitter<void>();
  @Output() draftSaved = new EventEmitter<StatementFormData>();
  @Output() picoImported = new EventEmitter<any>();

  statementForm: FormGroup;
  isSubmitting = false;
  progressMessage = '';

  specialtySearch = '';
  filteredSpecialties: string[] = [];

  // PICO selection
  picoMode: 'existing' | 'new' = 'new';
  selectedPicoId: number | null = null;

  // Literature selection
  showLiteratureSelector = false;
  selectedLiteratureIds: any[] = [];
  addedLiteratureIds: any[] = []; // Track which literature items are already added to anchor studies

  // Survey selection and import
  showSurveySelector = false;
  selectedSurveyIds: any[] = [];
  importedSurveys: any[] = [];

  // Medical specialties list
  medicalSpecialties: string[] = [
    'Allergologia ed immunologia clinica',
    'Anatomia patologica',
    'Anestesia e rianimazione',
    'Angiologia',
    'Assistente sanitario',
    'Audiologia e foniatria',
    'Biochimica clinica',
    'Biologo',
    'Cardiochirurgia',
    'Cardiologia',
    'Chimica analitica',
    'Chirurgia generale',
    'Chirurgia maxillo-facciale',
    'Chirurgia pediatrica',
    'Chirurgia plastica e ricostruttiva',
    'Chirurgia toracica',
    'Chirurgia vascolare',
    'Continuità assistenziale',
    'Dermatologia e venereologia',
    'Dietista',
    'Direzione medica di presidio ospedaliero',
    'Educatore professionale',
    'Ematologia',
    'Endocrinologia',
    'Farmacia ospedaliera',
    'Farmacia territoriale',
    'Farmacologia e tossicologia clinica',
    'Fisica sanitaria',
    'Fisioterapista',
    'Gastroenterologia',
    'Genetica medica',
    'Geriatria',
    'Ginecologia e ostetricia',
    'Igiene degli alimenti e della nutrizione',
    'Igiene degli allevamenti e delle produzioni zootecniche',
    'Igiene prod., trasf., commercial., conserv. E tras. Alimenti di origine animale e derivati',
    'Igiene, epidemiologia e sanità pubblica',
    'Igienista dentale',
    'Infermiere',
    'Infermiere pediatrico',
    'Laboratorio di genetica medica',
    'Logopedista',
    'Malattie dell\'apparato respiratorio',
    'Malattie infettive',
    'Malattie metaboliche e diabetologia',
    'Medicina aeronautica e spaziale',
    'Medicina alternative',
    'Medicina del lavoro e sicurezza degli ambienti di lavoro',
    'Medicina dello sport',
    'Medicina e chirurgia di accettazione e di urgenza',
    'Medicina fisica e riabilitazione',
    'Medicina generale (medici di famiglia)',
    'Medicina interna',
    'Medicina legale',
    'Medicina nucleare',
    'Medicina termale',
    'Medicina trasfusionale',
    'Microbiologia e virologia',
    'Nefrologia',
    'Neonatologia',
    'Neurochirurgia',
    'Neurofisiopatologia',
    'Neurologia',
    'Neuropsichiatria infantile',
    'Neuroradiologia',
    'Odontoiatria',
    'Oftalmologia',
    'Oncologia',
    'Organizzazione dei servizi sanitari di base',
    'Ortopedia e traumatologia',
    'Ortottista/assistente di oftalmologia',
    'Ostetrica/o',
    'Otorinolaringoiatria',
    'Patologia clinica (laboratorio di analisi chimico-cliniche e microbiologia)',
    'Pediatria',
    'Pediatria (pediatri di libera scelta)',
    'Podologo',
    'Psichiatria',
    'Psicologia',
    'Psicoterapia',
    'Radiodiagnostica',
    'Radioterapia',
    'Reumatologia',
    'Sanità animale',
    'Scienza dell\'alimentazione e dietetica',
    'Tecnico della prevenzione nell\'ambiente e nei luoghi di lavoro',
    'Tecnico della riabilitazione psichiatrica',
    'Tecnico di neurofisiopatologia',
    'Tecnico ortopedico',
    'Terapista della neuro e psicomotricità dell\'età evolutiva',
    'Terapista occupazionale',
    'Urologia',
    'Altro',
  ];

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private surveyService: SurveyService,
    private dialog: MatDialog
  ) {}

  /**
   * Open the read-only details dialog for an imported survey.
   */
  openSurveyDetails(survey: any): void {
    this.dialog.open(SurveyDetailsDialogComponent, {
      data: { survey },
      width: '520px',
      maxWidth: '90vw'
    });
  }

  ngOnInit(): void {
    this.initializeForm();
    this.populateFromProjectData();
    this.checkExistingPicos();
    this.filteredSpecialties = [...this.medicalSpecialties];
  }

  /**
   * Returns only analyzed literature (status processed/completed or has llm_analysis)
   */
  get analyzedLiterature(): any[] {
    if (!this.existingLiterature) return [];
    return this.existingLiterature.filter(lit =>
      !!lit.llm_analysis || !!lit.key_findings ||
      lit.status === 'processed' || lit.status === 'completed'
    );
  }

  /**
   * Whether all selectable (analyzed, not yet added) items are currently selected
   */
  get allLiteratureSelected(): boolean {
    const selectable = this.analyzedLiterature.filter(lit => !this.isLiteratureAlreadyAdded(lit.id));
    return selectable.length > 0 && selectable.every(lit => this.isLiteratureSelected(lit.id));
  }

  filterSpecialties(): void {
    const search = this.specialtySearch.toLowerCase().trim();
    this.filteredSpecialties = search
      ? this.medicalSpecialties.filter(s => s.toLowerCase().includes(search))
      : [...this.medicalSpecialties];
  }

  onSpecialtyPanelOpen(opened: boolean): void {
    if (!opened) {
      this.specialtySearch = '';
      this.filteredSpecialties = [...this.medicalSpecialties];
    }
  }

  /**
   * Initialize the reactive form with all sections
   */
  private initializeForm(): void {
    this.statementForm = this.fb.group({
      // A. IDENTITÀ
      projectTitle: ['', Validators.required],
      therapeuticArea: [''],
      consultationType: this.fb.group({
        asynchronousAdvisoryBoard: [false],
        delphi: [false],
        modifiedDelphi: [false],
        miniDelphi: [false],
        structuredExpertSurvey: [false],
        other: ['']
      }),
      surveyObjective: ['', Validators.required],
      sessionFocus: [''],
      targetAudience: this.fb.group({
        specialties: [[]],
        otherSpecialty: [''],
        kol: [false],
        expertClinicians: [false],
        mix: [false],
        hub: [false],
        spoke: [false],
        mixedSetting: [false],
        geography: ['']
      }),

      // B. PICO / PICOST
      pico: this.fb.group({
        population: [''],
        intervention: [''],
        comparator: [''],
        outcomes: [''],
        studies: [''],
        timeframe: ['']
      }),

      // C. CONTESTO CLINICO & UNMET NEEDS
      clinicalContext: this.fb.group({
        patientJourney: [''],
        unmetNeeds: ['']
      }),

      // D. EVIDENZE DA CONSIDERARE
      evidence: this.fb.group({
        anchorStudies: this.fb.array([]),
        evidenceTypes: this.fb.group({
          guidelines: [false],
          rct: [false],
          phase2: [false],
          phase1: [false],
          realWorld: [false],
          comparative: [false],
          biological: [false]
        }),
        exclusions: [''],
        previousSurveys: [false],
        selectedPreviousSurveys: [[]]
      }),

      // E. OUTPUT CONSTRAINTS
      targetStatements: [4, [Validators.min(1), Validators.max(20)]],

      // F. COMPLIANCE & STILE
      compliance: this.fb.group({
        strictEvidenceMode: [true],
        minimizeOffLabelTone: [true],
        epistemicTransparency: ['standard']
      }),

      // G. REGOLE EDITORIALI AVANZATE
      editorialRules: this.fb.group({
        toneNeutral: [false],
        toneDiscussion: [false],
        toneNonPromotional: [false],
        toneNoSuperlatives: [false],
        evidenceGrading: [''],
        granularitySingleConcept: [false],
        granularityNoRedundancy: [false],
        glossary: ['']
      })
    });
  }

  /**
   * Populate form with project data if available
   */
  private populateFromProjectData(): void {
    if (this.projectData) {
      this.statementForm.patchValue({
        projectTitle: this.projectData.title || '',
        therapeuticArea: this.projectData.therapeutic_area || this.projectData.contesto_clinico_scientifico || '',
        surveyObjective: this.projectData.obiettivi || ''
      });
    }

    if (this.picoData) {
      const picoUpdate: any = {
        population: this.picoData.population || '',
        intervention: this.picoData.intervention || '',
        comparator: this.picoData.comparison || this.picoData.comparator || '',
      };

      // Parse outcomes if available
      if (this.picoData.outcomes) {
        picoUpdate.outcomes = this.parseOutcomesFromText(
          this.picoData.outcomes,
        );
      }

      // Parse outcomes if available (picoData may carry it as `outcome`)
      if (this.picoData.outcomes || this.picoData.outcome) {
        picoUpdate.outcomes = this.parseOutcomesFromText(
          this.picoData.outcomes || this.picoData.outcome,
        );
      }

      // S – Studies (bound to the `studies` control); picoData may carry it as
      // `studies` or the legacy `setting` key
      const studies = this.picoData.studies || this.picoData.setting;
      if (studies) {
        picoUpdate.studies = this.parseSettingFromText(studies);
      }

      // T – Timeframe
      if (this.picoData.timeframe) {
        picoUpdate.timeframe = this.picoData.timeframe;
      }

      this.statementForm.patchValue({
        pico: picoUpdate,
      });
    }
  }

  /**
   * Get anchor studies form array
   */
  get anchorStudies(): FormArray {
    return this.statementForm.get('evidence.anchorStudies') as FormArray;
  }

  /**
   * Add a new anchor study to the form
   */
  addAnchorStudy(): void {
    const studyGroup = this.fb.group({
      reference: ['', Validators.required],
      relevance: ['', Validators.required]
    });
    this.anchorStudies.push(studyGroup);
  }

  /**
   * Remove an anchor study from the form
   */
  removeAnchorStudy(index: number): void {
    // Get the literature ID before removing
    const studyGroup = this.anchorStudies.at(index);
    const literatureId = studyGroup.get('literatureId')?.value;

    // Remove from form array
    this.anchorStudies.removeAt(index);

    // Remove from tracked IDs so it can be added again
    if (literatureId) {
      this.addedLiteratureIds = this.addedLiteratureIds.filter(id => id !== literatureId);
    }
  }

  /**
   * Handle form submission
   */
  onSubmit(): void {
    if (this.statementForm.invalid) {
      this.showMessage('Compila tutti i campi obbligatori', 'warning');
      this.markFormGroupTouched(this.statementForm);
      return;
    }

    const formData: StatementFormData = this.statementForm.getRawValue();
    const backendPayload = this.transformToBackendPayload(formData);

    this.isSubmitting = true;
    this.progressMessage = 'Waiting for statements generation...';
    this.formSubmitted.emit(backendPayload as any);
  }

  /**
   * Populate the form from a previously-sent backend payload (a process's
   * stored request_data). Inverse of transformToBackendPayload for the cleanly
   * reversible fields (identity, PICO, clinical context, output constraints,
   * compliance, editorial rules).
   *
   * Evidence selections (anchor studies / internal surveys) are NOT
   * reconstructed: the payload stores resolved studies/survey data, while the
   * form tracks selections by literature/survey id, so they must be re-picked.
   */
  populateFromBackendPayload(payload: any): void {
    if (!payload || !this.statementForm) return;

    const pc = payload.project_context || {};
    const pico = payload.picost_framework || {};
    const cc = payload.clinical_context || {};
    const ct = payload.compliance_toggles || {};
    const oc = payload.output_constraints || {};
    const er = payload.editorial_rules || {};
    const tone: string[] = Array.isArray(er.tone) ? er.tone : [];
    const gran: string[] = Array.isArray(er.granularity) ? er.granularity : [];

    const unmet = Array.isArray(cc.unmet_needs_ranked)
      ? cc.unmet_needs_ranked.join('\n')
      : (cc.unmet_needs_ranked || '');

    this.statementForm.patchValue({
      projectTitle: pc.title || '',
      therapeuticArea: pc.therapeutic_area || '',
      surveyObjective: pc.survey_objective || '',
      consultationType: this.consultationTypeFromString(pc.consultation_type || ''),
      pico: {
        population: pico.population || '',
        intervention: pico.intervention || '',
        comparator: pico.comparator || '',
        outcomes: pico.outcomes || '',
        studies: pico.studies || pico.setting || '',
        timeframe: pico.timeframe || ''
      },
      clinicalContext: {
        patientJourney: cc.patient_journey || '',
        unmetNeeds: unmet
      },
      targetStatements: oc.target_statements ?? 4,
      compliance: {
        strictEvidenceMode: ct.strict_evidence_mode ?? true,
        minimizeOffLabelTone: ct.minimize_off_label_tone ?? true,
        epistemicTransparency: ct.epistemic_transparency_level || 'standard'
      },
      editorialRules: {
        toneNeutral: tone.includes('neutral'),
        toneDiscussion: tone.includes('stimulate_discussion'),
        toneNonPromotional: tone.includes('non_promotional'),
        toneNoSuperlatives: tone.includes('no_superlatives'),
        evidenceGrading: er.evidence_grading || '',
        granularitySingleConcept: gran.includes('single_concept_per_statement'),
        granularityNoRedundancy: gran.includes('no_semantic_redundancy'),
        glossary: er.glossary || ''
      }
    });
  }

  /** Inverse of getConsultationType: backend string → checkbox group value. */
  private consultationTypeFromString(value: string): any {
    const base = {
      asynchronousAdvisoryBoard: false,
      delphi: false,
      modifiedDelphi: false,
      miniDelphi: false,
      structuredExpertSurvey: false,
      other: ''
    };
    switch ((value || '').trim()) {
      case 'Asynchronous Advisory Board': return { ...base, asynchronousAdvisoryBoard: true };
      case 'Delphi': return { ...base, delphi: true };
      case 'Modified Delphi': return { ...base, modifiedDelphi: true };
      case 'Mini Delphi': return { ...base, miniDelphi: true };
      case 'Structured Expert Survey': return { ...base, structuredExpertSurvey: true };
      case '': return base;
      default: return { ...base, other: value };
    }
  }

  /**
   * Transform form data to backend expected format
   */
  private transformToBackendPayload(formData: StatementFormData): any {
    // Build project context
    const projectContext = {
      title: formData.projectTitle || this.projectData?.title || '',
      therapeutic_area: formData.therapeuticArea || '',
      consultation_type: this.getConsultationType(formData.consultationType),
      survey_objective: formData.surveyObjective || ''
    };

    // Build PICOST framework
    const picostFramework = {
      population: formData.pico?.population || '',
      intervention: formData.pico?.intervention || '',
      comparator: formData.pico?.comparator || '',
      outcomes: this.getOutcomesText(formData.pico?.outcomes),
      // Outbound `setting` key kept for the generation Lambda contract; sourced
      // from the renamed `studies` control
      setting: this.getSettingText(formData.pico?.studies),
      timeframe: formData.pico?.timeframe || ''
    };

    // Build clinical context
    const clinicalContext = {
      patient_journey: formData.clinicalContext?.patientJourney || '',
      unmet_needs_ranked: this.getUnmetNeedsRanked(formData.clinicalContext?.unmetNeeds)
    };

    // Build evidence sources. anchor_studies falls back to the literal "N.A." when
    // no studies were selected, so the backend/Lambda gets an explicit "not available"
    // marker rather than an empty array.
    const anchorStudies = this.transformAnchorStudies(formData.evidence?.anchorStudies || []);
    const evidenceSources: any = {
      anchor_studies: anchorStudies.length > 0 ? anchorStudies : 'N.A.',
      internal_sources: this.transformInternalSources(formData.evidence?.selectedPreviousSurveys || [])
    };

    // Build final payload
    return {
      project_id: this.projectId,
      organization_id: this.projectData?.organization_id || null,
      project_context: projectContext,
      picost_framework: picostFramework,
      clinical_context: clinicalContext,
      evidence_sources: evidenceSources,
      compliance_toggles: {
        strict_evidence_mode: formData.compliance?.strictEvidenceMode ?? true,
        minimize_off_label_tone: formData.compliance?.minimizeOffLabelTone ?? true,
        epistemic_transparency_level: formData.compliance?.epistemicTransparency ?? 'standard'
      },
      output_constraints: {
        target_statements: formData.targetStatements ?? 4
      },
      editorial_rules: {
        tone: [
          formData.editorialRules?.toneNeutral && 'neutral',
          formData.editorialRules?.toneDiscussion && 'stimulate_discussion',
          formData.editorialRules?.toneNonPromotional && 'non_promotional',
          formData.editorialRules?.toneNoSuperlatives && 'no_superlatives'
        ].filter(Boolean),
        evidence_grading: formData.editorialRules?.evidenceGrading || '',
        granularity: [
          formData.editorialRules?.granularitySingleConcept && 'single_concept_per_statement',
          formData.editorialRules?.granularityNoRedundancy && 'no_semantic_redundancy'
        ].filter(Boolean),
        glossary: formData.editorialRules?.glossary || ''
      }
    };
  }

  /**
   * Get consultation type string
   */
  private getConsultationType(consultationType: any): string {
    if (consultationType.asynchronousAdvisoryBoard) return 'Asynchronous Advisory Board';
    if (consultationType.delphi) return 'Delphi';
    if (consultationType.modifiedDelphi) return 'Modified Delphi';
    if (consultationType.miniDelphi) return 'Mini Delphi';
    if (consultationType.structuredExpertSurvey) return 'Structured Expert Survey';
    if (consultationType.other) return consultationType.other;
    return '';
  }

  /**
   * Get outcomes text
   */
  private getOutcomesText(outcomes: string): string {
    return outcomes || '';
  }

  /**
   * Parse outcomes text to checkbox values
   */
  private parseOutcomesFromText(outcomesText: string): string {
    return outcomesText || '';
  }

  /**
   * Get setting text
   */
  private getSettingText(setting: string): string {
    return setting || '';
  }

  /**
   * Parse setting text
   */
  private parseSettingFromText(settingText: string): string {
    return settingText || '';
  }

  /**
   * Get unmet needs ranked
   */
  private getUnmetNeedsRanked(unmetNeeds: string): string[] {
    if (!unmetNeeds) return [];
    return unmetNeeds
      .split(/[\n,]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);
  }

  /**
   * Extract summary paragraph from llm_analysis
   * Format: agent_output.split("```json")[1].split("```")[1]
   */
  private extractSummaryFromLlmAnalysis(llmAnalysis: string): string | null {
    if (!llmAnalysis) {
      return null;
    }

    try {
      // Split by ```json and get the part after it
      const parts = llmAnalysis.split('```json');
      if (parts.length < 2) {
        console.warn('⚠️ llm_analysis does not contain ```json marker, returning as-is');
        return llmAnalysis;
      }

      // Split by ``` and get the summary paragraph (second part after the JSON block)
      const afterJson = parts[1].split('```');
      if (afterJson.length < 2) {
        console.warn('⚠️ llm_analysis does not have closing ``` marker, returning as-is');
        return llmAnalysis;
      }

      const summary = afterJson[1].trim();
      return summary;
    } catch (error) {
      console.error('Error extracting summary from llm_analysis:', error);
      return llmAnalysis; // Fallback to original
    }
  }

  /**
   * Transform anchor studies to backend format
   */
  private transformAnchorStudies(anchorStudies: any[]): any[] {
    return anchorStudies.map(study => {
      // Find the literature item by ID to get llm_analysis
      const literatureItem = this.existingLiterature.find(lit => lit.id === study.literatureId);

      // console.log(` Transforming anchor study:`, {
      //   study,
      //   literatureItem,
      //   hasLlmAnalysis: !!(literatureItem?.llm_analysis || literatureItem?.key_findings)
      // });

      // Extract PMID or DOI from reference
      let refId = '';
      if (literatureItem?.pmid) {
        refId = `pmid:${literatureItem.pmid}`;
      } else if (literatureItem?.doi) {
        refId = `doi:${literatureItem.doi}`;
      }

      // Extract summary paragraph from llm_analysis
      const rawLlmAnalysis = literatureItem?.llm_analysis || literatureItem?.key_findings;
      const summaryParagraph = rawLlmAnalysis ? this.extractSummaryFromLlmAnalysis(rawLlmAnalysis) : null;

      // console.log(`LLM Analysis processing:`, {
      //   hasRawAnalysis: !!rawLlmAnalysis,
      //   hasSummary: !!summaryParagraph,
      //   summaryPreview: summaryParagraph?.substring(0, 50)
      // });

      return {
        ref_id: refId,
        title: literatureItem?.title || study.reference.split('|')[0]?.trim() || '',
        extracted_text: summaryParagraph || study.relevance
        // Note: llm_analysis field removed - only extracted_text is needed in payload
        // llm_analysis: summaryParagraph
      };
    });
  }

  /**
   * Trasforma le fonti interne (survey selezionate) nel formato atteso dal backend.
   * Invia al generatore le domande della survey + la risposta di ogni utente,
   * insieme all'analisi AI per domanda e a tutte le analisi LLM degli allegati
   * (a livello survey, domanda e risposta), così che il modello veda insieme le
   * risposte grezze, l'analisi aggregata e il contesto degli allegati elaborati.
   */
  private transformInternalSources(selectedSurveyIds: number[]): any[] {
    return selectedSurveyIds.map(surveyId => {
      const importedSurvey = this.importedSurveys.find(s => s.id === surveyId);
      const survey = importedSurvey || this.previousSurveysAvailable.find(s => s.id === surveyId);

      const questionsWithResponses = (importedSurvey?.questions || []).map((q: any) => ({
        id: q.id,
        question: q.question,
        description: q.description,
        typology: q.typology,
        choices: q.choices,
        responses: (q.responses || []).map((r: any) => ({
          id: r.id,
          respondent: r.name,
          answer: r.answer,
          feedback: r.feedback,
          confidence: r.confidence,
          score: r.score,
          magnitude: r.magnitude,
          choice_answers: r.choice_answers,
          open_response_value: r.open_response_value,
          attachments: r.attachments
        }))
      }));

      const attachments = (importedSurvey?.attachments || []).map((a: any) => ({
        id: a.id,
        attachment_id: a.attachment_id,
        scope: a.survey_question_answer_id
          ? 'answer'
          : a.survey_question_id ? 'question' : 'survey',
        survey_question_id: a.survey_question_id,
        survey_question_answer_id: a.survey_question_answer_id,
        type: a.type,
        doi: a.doi,
        link: a.link,
        state: a.state,
        llm_analysis: a.llm_analysis
      }));

      const internalSource = {
        ref_id: `internal_survey:isurv_${surveyId}`,
        title: survey?.name || survey?.title || `Survey ${surveyId}`,
        description: survey?.description || '',
        questions_count: survey?.questions_count ?? questionsWithResponses.length,
        responses_count: survey?.responses_count ?? 0,
        attachments_count: attachments.length,
        questions: questionsWithResponses,
        attachments,
        ai_summary: survey?.summary || 'Survey data',
        ai_analysis: survey?.ai_analysis || null
      };

      console.log(`Internal source created:`, internalSource);

      return internalSource;
    });
  }

  /**
   * Save form as draft
   */
  onSaveDraft(): void {
    const formData: StatementFormData = this.statementForm.value;
    this.draftSaved.emit(formData);
    this.showMessage('Bozza salvata con successo', 'success');
  }

  /**
   * Reset all form data back to initial state. The form intentionally persists
   * after generation completes/fails, so this button gives the user an explicit
   * way to start fresh.
   */
  onReset(): void {
    if (!confirm('Sei sicuro di voler resettare tutti i dati del form?')) {
      return;
    }

    // FormArray needs explicit clearing — reset() doesn't touch FormArray contents
    this.anchorStudies.clear();

    // Reset all simple fields, then re-apply the non-empty defaults from initializeForm()
    this.statementForm.reset();
    this.statementForm.patchValue({
      targetStatements: 4,
      compliance: {
        strictEvidenceMode: true,
        minimizeOffLabelTone: true,
        epistemicTransparency: 'standard'
      }
    });

    // Drop tracked extras
    this.addedLiteratureIds = [];
    this.importedSurveys = [];
    this.selectedLiteratureIds = [];
    this.selectedSurveyIds = [];
    this.showLiteratureSelector = false;
    this.showSurveySelector = false;

    this.populateFromProjectData();

    this.showMessage('Form resettato', 'success');
  }

  /**
   * Cancel form
   */
  onCancel(): void {
    if (this.statementForm.dirty) {
      if (confirm('Hai modifiche non salvate. Sei sicuro di voler uscire?')) {
        this.formCanceled.emit();
      }
    } else {
      this.formCanceled.emit();
    }
  }

  /**
   * Mark all form fields as touched to show validation errors
   */
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control.markAsTouched();

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach(c => {
          if (c instanceof FormGroup) {
            this.markFormGroupTouched(c);
          }
        });
      }
    });
  }

  /**
   * Show snackbar message
   */
  private showMessage(message: string, type: 'success' | 'error' | 'warning'): void {
    this.snackBar.open(message, '', {
      duration: 3000,
      panelClass: [`snackbar-${type}`],
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  /**
   * Reset form submission state
   */
  resetSubmissionState(): void {
    this.isSubmitting = false;
    this.progressMessage = '';
  }

  /**
   * Get form data as JSON
   */
  getFormData(): StatementFormData {
    return this.statementForm.value;
  }

  /**
   * Load form data from saved draft
   */
  loadFormData(data: Partial<StatementFormData>): void {
    this.statementForm.patchValue(data);

    // Restore anchor studies
    if (data.evidence?.anchorStudies) {
      this.anchorStudies.clear();
      data.evidence.anchorStudies.forEach(study => {
        const studyGroup = this.fb.group({
          reference: [study.reference, Validators.required],
          relevance: [study.relevance, Validators.required]
        });
        this.anchorStudies.push(studyGroup);
      });
    }
  }

  /**
   * Check if project has existing PICOs and set initial mode
   */
  private checkExistingPicos(): void {
    // If existingPicos are provided and picoData is not, default to 'existing' mode
    if (this.existingPicos && this.existingPicos.length > 0 && !this.picoData) {
      this.picoMode = 'existing';
      this.disablePicoFields();
    } else {
      // If picoData is provided or no existing PIcos, use 'new' mode
      this.picoMode = 'new';
      this.enablePicoFields();
    }
  }

  /**
   * Check if "Altro" is selected in specialties
   */
  isOtherSpecialtySelected(): boolean {
    const specialties = this.statementForm.get('targetAudience.specialties')?.value || [];
    return specialties.includes('Altro');
  }

  /**
   * Handle PICO mode change
   */
  onPicoModeChange(mode: 'existing' | 'new'): void {
    this.picoMode = mode;
    if (this.picoMode === 'new') {
      this.selectedPicoId = null;
      this.enablePicoFields();
      const picoGroup = this.statementForm.get('pico') as FormGroup;
      picoGroup.setValue({
        population: '',
        intervention: '',
        comparator: '',
        outcomes: '',
        studies: '',
        timeframe: ''
      });
    } else {
      // Import data from the PICO tab fields filled by the user
      const src = this.currentPicoTabData;
      if (src) {
        this.statementForm.patchValue({
          pico: {
            population: src.population || '',
            intervention: src.intervention || '',
            comparator: src.comparison || src.comparator || '',
            outcomes: src.outcomes || src.outcome || '',
            studies: src.studies || src.setting || '',
            timeframe: src.timeframe || '',
          },
        });
      }
      this.enablePicoFields();
    }
  }

  /**
   * Handle PICO selection from dropdown
   */
  onPicoSelected(picoId: number | null): void {
    if (!picoId) {
      // No PICO selected, clear fields
      this.statementForm.patchValue({
        pico: {
          population: '',
          intervention: '',
          comparator: ''
        }
      });
      this.disablePicoFields();
      return;
    }

    // Find selected PICO
    const selectedPico = this.existingPicos.find(p => p.id === picoId);
    if (selectedPico) {

      const picoUpdate: any = {
        population: selectedPico.population || '',
        intervention: selectedPico.intervention || '',
        comparator: selectedPico.comparator || selectedPico.comparison || ''
      };

      // Parse outcomes if available
      if (selectedPico.outcomes) {
        picoUpdate.outcomes = this.parseOutcomesFromText(selectedPico.outcomes);
      } else if (selectedPico.outcome) {
        picoUpdate.outcomes = this.parseOutcomesFromText(selectedPico.outcome);
      }

      // Parse studies (S field, bound to the `studies` control) if available
      const studies = selectedPico.studies || selectedPico.setting;
      if (studies) {
        picoUpdate.studies = this.parseSettingFromText(studies);
      }

      // Import timeframe if available
      if (selectedPico.timeframe) {
        picoUpdate.timeframe = selectedPico.timeframe;
      }

      // Populate form with selected PICO data
      this.statementForm.patchValue({
        pico: picoUpdate
      });

      this.disablePicoFields();
    }
  }

  /**
   * Enable PICO form fields for editing
   */
  private enablePicoFields(): void {
    const picoGroup = this.statementForm.get('pico');
    if (picoGroup) {
      picoGroup.get('population')?.enable();
      picoGroup.get('intervention')?.enable();
      picoGroup.get('comparator')?.enable();
      picoGroup.get('outcomes')?.enable();
      picoGroup.get('studies')?.enable();
      picoGroup.get('timeframe')?.enable();
    }
  }

  /**
   * Disable PICO form fields (read-only when selecting existing)
   */
  private disablePicoFields(): void {
    const picoGroup = this.statementForm.get('pico');
    if (picoGroup) {
      picoGroup.get('population')?.disable();
      picoGroup.get('intervention')?.disable();
      picoGroup.get('comparator')?.disable();
    }
  }

  /**
   * Open literature selector
   */
  openLiteratureSelector(): void {
    this.showLiteratureSelector = true;
    this.selectedLiteratureIds = [];
  }

  /**
   * Close literature selector
   */
  closeLiteratureSelector(): void {
    this.showLiteratureSelector = false;
    this.selectedLiteratureIds = [];
  }

  /**
   * Check if literature item is selected
   */
  isLiteratureSelected(litId: any): boolean {
    return this.selectedLiteratureIds.includes(litId);
  }

  /**
   * Check if literature item is already added to anchor studies
   */
  isLiteratureAlreadyAdded(litId: any): boolean {
    return this.addedLiteratureIds.includes(litId);
  }

  /**
   * Toggle select all / deselect all for analyzed, not-yet-added literature
   */
  selectAllLiterature(): void {
    if (this.allLiteratureSelected) {
      this.selectedLiteratureIds = [];
    } else {
      this.selectedLiteratureIds = this.analyzedLiterature
        .filter(lit => !this.isLiteratureAlreadyAdded(lit.id))
        .map(lit => lit.id);
    }
  }

  /**
   * Toggle literature selection
   */
  toggleLiterature(literature: any, checked: boolean): void {
    if (checked) {
      if (!this.selectedLiteratureIds.includes(literature.id)) {
        this.selectedLiteratureIds.push(literature.id);
      }
    } else {
      this.selectedLiteratureIds = this.selectedLiteratureIds.filter(id => id !== literature.id);
    }
  }

  /**
   * Add single literature item as anchor study
   */
  addLiteratureAsAnchorStudy(literature: any): void {
    // Check if already added
    if (this.addedLiteratureIds.includes(literature.id)) {
      console.log(`Literature ${literature.id} already added, skipping`);
      return;
    }

    const reference = this.buildLiteratureReference(literature);
    const relevance = literature.key_findings || literature.abstract || 'Studio rilevante dal database del progetto';

    const studyGroup = this.fb.group({
      reference: [reference, Validators.required],
      relevance: [relevance.substring(0, 200), Validators.required],
      literatureId: [literature.id] // Store literature ID for tracking
    });
    this.anchorStudies.push(studyGroup);

    // Track that this literature has been added
    this.addedLiteratureIds.push(literature.id);
  }

  /**
   * Add all selected literature as anchor studies
   */
  addSelectedLiterature(): void {
    const selectedLiterature = this.existingLiterature.filter(lit =>
      this.selectedLiteratureIds.includes(lit.id)
    );

    const beforeCount = this.anchorStudies.length;

    selectedLiterature.forEach(lit => {
      this.addLiteratureAsAnchorStudy(lit);
    });

    const addedCount = this.anchorStudies.length - beforeCount;
    const skippedCount = selectedLiterature.length - addedCount;

    this.closeLiteratureSelector();

    if (skippedCount > 0) {
      this.showMessage(`${addedCount} studi aggiunti, ${skippedCount} già presenti`, 'success');
    } else {
      this.showMessage(`${addedCount} studi aggiunti`, 'success');
    }
  }

  /**
   * Build reference string from literature item
   */
  private buildLiteratureReference(literature: any): string {
    const parts: string[] = [];

    if (literature.title) {
      parts.push(literature.title);
    }

    if (literature.authors) {
      parts.push(`Autori: ${literature.authors}`);
    }

    if (literature.year) {
      parts.push(`Anno: ${literature.year}`);
    }

    if (literature.pmid) {
      parts.push(`PMID: ${literature.pmid}`);
    }

    if (literature.doi) {
      parts.push(`DOI: ${literature.doi}`);
    }

    if (literature.nct) {
      parts.push(`NCT: ${literature.nct}`);
    }

    return parts.join(' | ');
  }

  /**
   * Open survey selector
   */
  openSurveySelector(): void {
    this.showSurveySelector = true;
    this.selectedSurveyIds = [];
  }

  /**
   * Close survey selector
   */
  closeSurveySelector(): void {
    this.showSurveySelector = false;
    this.selectedSurveyIds = [];
  }

  /**
   * Check if survey is selected
   */
  isSurveySelected(surveyId: any): boolean {
    return this.selectedSurveyIds.includes(surveyId);
  }

  /**
   * Toggle survey selection
   */
  toggleSurvey(survey: any, checked: boolean): void {
    if (checked) {
      if (!this.selectedSurveyIds.includes(survey.id)) {
        this.selectedSurveyIds.push(survey.id);
      }
    } else {
      this.selectedSurveyIds = this.selectedSurveyIds.filter(id => id !== survey.id);
    }
  }

  /**
   * Import selected surveys with full data
   */
  importSelectedSurveys(): void {
    const surveysToImport = this.previousSurveysAvailable.filter(survey =>
      this.selectedSurveyIds.includes(survey.id)
        && !this.importedSurveys.find(s => s.id === survey.id)
    );

    if (surveysToImport.length === 0) {
      return;
    }

    // console.log('Importing surveys:', surveysToImport.map(s => s.id));
    this.showMessage('Caricamento dati survey in corso...', 'success');

    Promise.all(surveysToImport.map(survey => this.loadSurveyForImport(survey)))
      .then(loaded => {
        loaded.forEach(s => this.importedSurveys.push(s));
        this.finalizeSurveyImport(surveysToImport.length);
      });
  }

  private loadSurveyForImport(survey: any): Promise<any> {
    const responsesP = new Promise<any>((resolve) => {
      this.surveyService.surveyResponses(
        survey.id.toString(),
        1,
        100,
        (result: any) => resolve(result),
        (error: any) => {
          console.error(`Failed to fetch survey responses for survey ${survey.id}:`, error);
          resolve(null);
        }
      );
    });

    const aiAnalysisP = new Promise<any>((resolve) => {
      this.surveyService.getSurveyAIAllQuestions(
        survey.id,
        (data: any) => resolve(data),
        (error: any) => {
          console.warn(`Failed to fetch AI analysis for survey ${survey.id}:`, error);
          resolve(null);
        }
      );
    });

    const attachmentsP = new Promise<any>((resolve) => {
      this.surveyService.getAllSurveyAttachmentsLLMAnalysis(
        survey.id,
        (data: any) => resolve(data),
        (error: any) => {
          console.warn(`Failed to fetch attachments LLM analysis for survey ${survey.id}:`, error);
          resolve(null);
        }
      );
    });

    return Promise.all([responsesP, aiAnalysisP, attachmentsP]).then(([result, aiAnalysisData, attachmentsData]) => {
      // surveyResponses() restituisce Laravel paginator { data: [...questions], total, ... }
      const questions = result?.data || result?.questions || result?.survey?.questions || [];
      const attachments = attachmentsData?.attachments || [];

      // The attachments endpoint returns total_count + a state_summary
      // { waiting, processing, completed, failed }
      const stateSummary = attachmentsData?.state_summary || {};

      // getSurveyAIAllQuestions() returns [{ question_id, analysis_result }] — an
      // entry has a real analysis only when analysis_result is non-null.
      const aiAnalyses = Array.isArray(aiAnalysisData) ? aiAnalysisData : [];
      const aiCompleted = aiAnalyses.filter((a: any) => a && a.analysis_result).length;

      return {
        id: survey.id,
        title: survey.title,
        description: survey.description,
        status: survey.status,
        questions,
        attachments,
        ai_analysis: aiAnalysisData,
        questions_count: result?.questions_count ?? result?.total ?? questions.length,
        responses_count: result?.respondents_count ?? 0,
        drafts_count: result?.drafts_count ?? 0,
        members_count: result?.members_count ?? 0,
        pending_count: result?.pending_count ?? 0,
        attachments_count: attachmentsData?.total_count ?? attachments.length,
        attachments_completed: stateSummary.completed ?? 0,
        attachments_waiting: stateSummary.waiting ?? 0,
        attachments_failed: stateSummary.failed ?? 0,
        attachments_processing: stateSummary.processing ?? 0,
        ai_analysis_total: aiAnalyses.length,
        ai_analysis_completed: aiCompleted,
        members: result?.survey?.members || survey.members || [],
        completion_rate: result?.survey?.completion_rate || survey.completion_rate || 0,
        created_at: survey.created_at,
        closed_at: survey.closed_at
      };
    });
  }

  /**
   * Finalize survey import after all data is loaded
   */
  private finalizeSurveyImport(count: number): void {
    // Update form control with imported survey IDs
    const importedIds = this.importedSurveys.map(s => s.id);
    this.statementForm.patchValue({
      evidence: {
        selectedPreviousSurveys: importedIds
      }
    });

    this.closeSurveySelector();

    // Show success message with details
    const totalQuestions = this.importedSurveys.reduce((sum, s) => sum + (s.questions_count || 0), 0);
    const totalResponses = this.importedSurveys.reduce((sum, s) => sum + (s.responses_count || 0), 0);
    const totalAttachments = this.importedSurveys.reduce((sum, s) => sum + (s.attachments_count || 0), 0);

    this.showMessage(
      ` ${count} survey importate (${totalQuestions} domande, ${totalResponses} risposte, ${totalAttachments} allegati)`,
      'success'
    );

  }

  /**
   * Remove imported survey
   */
  removeImportedSurvey(surveyId: any): void {
    this.importedSurveys = this.importedSurveys.filter(s => s.id !== surveyId);

    // Update form control
    const importedIds = this.importedSurveys.map(s => s.id);
    this.statementForm.patchValue({
      evidence: {
        selectedPreviousSurveys: importedIds
      }
    });
  }

  /**
   * Handle previous surveys checkbox change
   */
  onPreviousSurveysCheckboxChange(checked: boolean): void {
    if (checked) {
      // Checkbox checked - open modal to select surveys
      if (this.previousSurveysAvailable && this.previousSurveysAvailable.length > 0) {
        this.openSurveySelector();
      } else {
        // No surveys available
        this.showMessage('Nessuna survey disponibile per questo progetto', 'warning');
        // Uncheck the checkbox
        this.statementForm.patchValue({
          evidence: {
            previousSurveys: false
          }
        });
      }
    } else {
      // Checkbox unchecked - clear imported surveys
      this.importedSurveys = [];
      this.statementForm.patchValue({
        evidence: {
          selectedPreviousSurveys: []
        }
      });
    }
  }
}

